/**
 * A file picker that is also a drop target.
 *
 * Layout follows Osvaldas Valutis's pattern from CSS-Tricks ("Drag and Drop
 * File Uploading"), at roughly a fifth of its size — that article's box is a
 * page feature, and this is one field among six.
 *
 * WHAT IS BORROWED, and why each part earns its place:
 *
 *   The dashed rule is an OUTLINE with a negative offset, not a border. It
 *   therefore sits inside the box without taking part in its size, so the
 *   padding stays honest and nothing shifts when the state changes.
 *
 *   The <input type="file"> is visually hidden but still in the document, still
 *   focusable, still the thing that opens the picker. Browsers will not let you
 *   style it, so the visible control is its <label> — clicking the label is
 *   what a label does, and no JavaScript is involved in that part at all. This
 *   is the piece worth copying most: it replaces an unstyleable control without
 *   replacing the control.
 *
 *   " or drag it here" appears only when the browser can actually do it. On a
 *   tablet the sentence is "Choose a file." rather than an instruction for a
 *   gesture that does not exist there.
 *
 * WHAT IS NOT BORROWED: the article hides the input with `display: none` in
 * places. That takes it out of the tab order, which is the one thing the whole
 * arrangement exists to preserve. It is clipped here instead, and the label
 * carries a visible ring whenever the hidden input has focus — otherwise a
 * keyboard user tabs onto something they cannot see.
 *
 * The drag handling is ours and is unchanged: preventDefault on dragover as
 * well as drop, or the browser navigates to the file and the form is lost; and
 * a depth counter rather than a boolean, or the highlight flickers off as the
 * pointer crosses the label inside the zone.
 */

import { useRef, useState, type DragEvent, type RefObject } from 'react';
import styles from './FileDrop.module.css';

export interface FileDropProps {
  id: string;
  onFile?: (file: File) => void;
  /**
   * Accept several at once. `onFiles` then receives every file of a drop or a
   * multi-selection, in the order the browser gives them.
   */
  multiple?: boolean;
  onFiles?: (files: File[]) => void;
  /** The file already chosen, so the box can name it. */
  file?: File | null;
  accept?: string;
  disabled?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  invalid?: boolean;
  describedBy?: string;
  /** Overrides "Choose a file" — the bold half of the prompt. */
  prompt?: string;
  /**
   * Added to the box. For a page where the document IS the task and the
   * target should read that way — the single upload gives it a tall one —
   * rather than for restyling the component from outside.
   */
  className?: string;
}

/**
 * Whether dragging a file onto the page means anything here.
 *
 * The article's check, unchanged. A first version also excluded anything
 * reporting touch, on the theory that a tablet has no drag gesture that
 * reaches a page — but every touchscreen laptop reports touch too, and those
 * have a mouse and drag perfectly well. Hiding the offer from them was the
 * bigger mistake. A tablet that sees " or drag it here" and cannot do it has
 * lost nothing; the picker still works.
 */
export function supportsDragAndDrop(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  const probe = document.createElement('div');
  const draggable = 'draggable' in probe || ('ondragstart' in probe && 'ondrop' in probe);
  return draggable && 'FormData' in window && 'FileReader' in window;
}

/**
 * The first file of a drop, or null.
 *
 * A drop can carry several files, a folder, or dragged text that is not a file
 * at all. Taking the first and ignoring the rest beats refusing outright and
 * beats uploading something nobody chose; validation downstream rejects it with
 * a reason if it is wrong.
 */
export function allFilesFrom(transfer: DataTransfer | null): File[] {
  if (!transfer) return [];

  if (transfer.items?.length) {
    const files: File[] = [];
    for (const item of Array.from(transfer.items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    return files;
  }

  return Array.from(transfer.files ?? []);
}

export function firstFileFrom(transfer: DataTransfer | null): File | null {
  return allFilesFrom(transfer)[0] ?? null;
}

export function FileDrop({
  id,
  onFile,
  multiple = false,
  onFiles,
  file = null,
  accept,
  disabled = false,
  inputRef,
  invalid,
  describedBy,
  prompt = 'Choose a file',
  className,
}: FileDropProps) {
  // Depth, not a boolean. See the note at the top.
  const depth = useRef(0);
  const [over, setOver] = useState(false);
  const [focused, setFocused] = useState(false);

  // Decided once, when the component is created: the check touches `window`,
  // which a single-page app always has (there is no server render to match).
  const [canDrag] = useState(() => supportsDragAndDrop());

  function stop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  const classes = [styles.box, over && styles.over, focused && styles.focused, disabled && styles.disabled, className]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      data-over={over || undefined}
      data-focused={focused || undefined}
      onDragEnter={(e) => {
        stop(e);
        if (disabled || !canDrag) return;
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(e) => {
        // Required even though it looks redundant: without it the drop never
        // fires and the browser navigates to the file instead.
        stop(e);
        if (!disabled && canDrag) e.dataTransfer.dropEffect = 'copy';
      }}
      onDragLeave={(e) => {
        stop(e);
        if (disabled || !canDrag) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      }}
      onDrop={(e) => {
        stop(e);
        depth.current = 0;
        setOver(false);
        if (disabled || !canDrag) return;

        if (multiple) {
          const dropped = allFilesFrom(e.dataTransfer);
          if (dropped.length) onFiles?.(dropped);
        } else {
          const dropped = firstFileFrom(e.dataTransfer);
          if (dropped) onFile?.(dropped);
        }
      }}
    >
      <input
        id={id}
        ref={inputRef}
        type="file"
        className={styles.input}
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          const list = Array.from(e.target.files ?? []);
          if (multiple) {
            if (list.length) onFiles?.(list);
            // So the same files can be chosen again after being removed from
            // a queue; a file input ignores a re-pick of its current value.
            e.target.value = '';
          } else if (list[0]) {
            onFile?.(list[0]);
          }
        }}
      />

      <label htmlFor={id} className={styles.label}>
        <svg className={styles.icon} width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <span className={styles.text}>
          {file ? (
            <>
              <strong className={styles.strong}>{file.name}</strong>
              <span className={styles.swap}> — choose another</span>
            </>
          ) : (
            <>
              <strong className={styles.strong}>{prompt}</strong>
              {/* "them" where the control takes several, so a prompt naming a
                  plural does not end "…or drag it here". */}
              {canDrag && (
                <span className={styles.drag}>{multiple ? ' or drag them here' : ' or drag it here'}</span>
              )}
            </>
          )}
        </span>
      </label>
    </div>
  );
}
